import { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { API } from "@/lib/api";

function fmtNum(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return v.toLocaleString();
}

function fmtUsd(n) {
  const v = Number(n || 0);
  if (v === 0) return "$0";
  if (v < 0.01) return `$${v.toFixed(4)}`;
  return `$${v.toFixed(2)}`;
}

function fmtRel(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.max(0, Math.floor(diff / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function AdminModelTokenUsageSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/usage/tokens/models`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(err.message || "Failed to load model tokens");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = data?.rows || [];
    if (!q) return base;
    return base.filter((r) => `${r.engine} ${r.model}`.toLowerCase().includes(q));
  }, [data, query]);

  if (loading && !data) {
    return (
      <div data-testid="model-token-loading" className="py-10 text-center font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-dim)]">
        <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading model token ledger…
      </div>
    );
  }

  return (
    <div data-testid="admin-model-token-usage" className="space-y-5">
      {error && <div data-testid="model-token-error" className="border border-red-500/60 text-red-500 p-3 text-sm">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile testid="model-tokens-24h" label="Daily tokens" value={fmtNum(data?.totals?.tokens_24h)} sub={`${fmtNum(data?.totals?.calls_24h)} calls · 24h`} />
        <Tile testid="model-tokens-7d" label="Weekly tokens" value={fmtNum(data?.totals?.tokens_7d)} sub={`${fmtNum(data?.totals?.calls_7d)} calls · 7d`} />
        <Tile testid="model-tokens-30d" label="Monthly tokens" value={fmtNum(data?.totals?.tokens_30d)} sub={`${fmtNum(data?.totals?.calls_30d)} calls · 30d`} />
        <Tile testid="model-tokens-cost" label="Monthly cost" value={fmtUsd(data?.totals?.cost_30d)} sub={`${rows.length} models recorded`} />
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between border border-[var(--border)] p-4">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter engine or model"
          data-testid="model-token-filter"
          className="w-full md:max-w-sm bg-transparent border border-[var(--border)] px-3 py-2 font-mono-ui text-xs outline-none focus:border-[var(--accent)]"
        />
        <button
          onClick={load}
          disabled={loading}
          data-testid="model-token-refresh"
          className="px-4 py-2 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] font-mono-ui text-[10px] tracking-[0.24em] uppercase inline-flex items-center gap-2 disabled:opacity-50"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
        </button>
      </div>

      <div className="border border-[var(--border)] overflow-x-auto" data-testid="model-token-table-wrap">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border)] text-left">
              <Th>Engine</Th>
              <Th>Model</Th>
              <Th className="text-right">Daily</Th>
              <Th className="text-right">Weekly</Th>
              <Th className="text-right">Monthly</Th>
              <Th className="text-right hidden md:table-cell">Calls</Th>
              <Th className="text-right hidden lg:table-cell">Prompt / Output</Th>
              <Th className="text-right">Cost</Th>
              <Th className="hidden md:table-cell">Last used</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-[var(--text-dim)] font-mono-ui text-[10px] tracking-[0.24em] uppercase">No model token records yet.</td></tr>
            ) : rows.map((r) => (
              <tr key={`${r.engine}:${r.model}`} data-testid={`model-token-row-${r.engine}`} className="border-b border-[var(--border)] last:border-b-0">
                <Td className="font-mono-ui text-[10px] tracking-[0.18em] uppercase">{r.engine}</Td>
                <Td><span className="font-mono-ui text-xs break-all">{r.model}</span></Td>
                <Td className="text-right font-mono-ui">{fmtNum(r.tokens_24h)}</Td>
                <Td className="text-right font-mono-ui">{fmtNum(r.tokens_7d)}</Td>
                <Td className="text-right font-mono-ui">{fmtNum(r.tokens_30d)}</Td>
                <Td className="text-right font-mono-ui hidden md:table-cell">{fmtNum(r.calls_30d)}</Td>
                <Td className="text-right font-mono-ui hidden lg:table-cell">{fmtNum(r.prompt_tokens_30d)} / {fmtNum(r.completion_tokens_30d)}</Td>
                <Td className="text-right font-mono-ui">{fmtUsd(r.cost_30d)}</Td>
                <Td className="hidden md:table-cell font-mono-ui text-[10px] tracking-[0.16em] uppercase text-[var(--text-dim)]">{fmtRel(r.last_used)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Tile({ label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="border border-[var(--border)] px-4 py-3">
      <div className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">{label}</div>
      <div className="font-mono-ui text-xl text-[var(--text)] mt-1">{value}</div>
      <div className="font-mono-ui text-[9px] tracking-[0.16em] uppercase text-[var(--text-dim)] mt-1">{sub}</div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] ${className}`}>{children}</th>;
}

function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 text-[var(--text)] ${className}`}>{children}</td>;
}